import { useMemo } from 'react';

import { AuthStepType } from '@/shared/types/tryIt';
import { walkAuthSubAgentConfigs } from '@/util/auth/walkAuthSubAgentConfigs';

import { useDialogsList } from '../../../hooks/useDialogsList';
import { TurnAuthentication } from '../../../types/conversation';

// `undefined` when the brain configures no such step.
const usePointsByQuestion = (step?: AuthStepType) => {
  const dialogs = useDialogsList();

  return useMemo(() => {
    if (step !== AuthStepType.IDENTIFICATION && step !== AuthStepType.VERIFICATION) {
      return undefined;
    }
    const points = new Map<string, number>();
    let configured = false;
    for (const { config } of walkAuthSubAgentConfigs(dialogs)) {
      const authStep =
        step === AuthStepType.IDENTIFICATION
          ? config.identification_step
          : config.verification_step;
      if (!authStep) {
        continue;
      }
      configured = true;
      for (const question of authStep.questions ?? []) {
        points.set(question.save_as, question.points);
      }
    }
    return configured ? points : undefined;
  }, [dialogs, step]);
};

// The agent scores one step at a time, so only the current step's questions count.
export const useAuthScore = (auth: TurnAuthentication): number | undefined => {
  const points = usePointsByQuestion(auth.current_step);

  if (!auth.scoreUnknown) {
    return auth.score ?? 0;
  }
  if (!points) {
    return undefined;
  }
  return (auth.validated_question_keys ?? []).reduce(
    (total, key) => total + (points.get(key) ?? 0),
    0
  );
};
